
import { GameResultScreen } from "../screens/GameResultScreen";
import type { DailyPuzzle } from "@/lib/types/game";
import type { LogicRule } from "@/lib/game-utils";
import type { GameResult } from "@/lib/types/game";
import { useEffect, useState } from "react";
import { getLastGameResult, isViewingResults, isGameCompleted, markGameCompleted } from "@/lib/storage/local-storage";

interface GameResultContainerProps {
  gameResult?: GameResult;
  puzzle: DailyPuzzle | null;
  filteredRules?: LogicRule[];
  isUsingExternalData?: boolean;
  userEmail: string | null;
  onGoBack?: () => void;
}

export function GameResultContainer({ 
  gameResult, 
  puzzle, 
  filteredRules = [], 
  isUsingExternalData = false,
  userEmail,
  onGoBack
}: GameResultContainerProps) {
  // State to hold the final result to display
  const [resultToDisplay, setResultToDisplay] = useState<GameResult | null>(null);
  
  // Effect to determine which result to display
  useEffect(() => {
    console.log("GameResultContainer checking for results to display");
    
    // Check for a directly provided game result first (highest priority)
    if (gameResult) {
      console.log("Using directly provided game result:", gameResult.result);
      setResultToDisplay(gameResult);
      return;
    }
    
    // Try to get saved result from localStorage
    const savedResult = getLastGameResult();
    if (savedResult) {
      console.log("Using saved game result from localStorage:", savedResult.result);
      setResultToDisplay(savedResult);
      // Ensure the completion status is properly set
      if (savedResult.completedDate && puzzle?.date === savedResult.completedDate) {
        markGameCompleted(savedResult.completedDate);
      }
      return;
    }
    
    // If nothing found and we have a puzzle, create a default result
    if (puzzle) {
      console.log("Creating default game result");
      const defaultResult: GameResult = {
        result: "wrong", // Default to wrong result
        solveTime: 0,
        selectedWords: puzzle?.words?.map((word, index) => ({ word, order: index + 1 })) || [],
        selectedRule: puzzle?.correctRule ? {
          ...puzzle.correctRule,
          sortFn: puzzle.correctRule.sortFn || ((a: string, b: string) => 0),
          category: puzzle.correctRule.category || "spreadsheet"
        } : {
          id: "",
          name: "",
          description: "",
          sortFn: (a: string, b: string) => 0,
          category: "spreadsheet"
        },
        completedDate: puzzle?.date
      };
      setResultToDisplay(defaultResult);
    }
  }, [gameResult, puzzle]);

  // Log what data we're using
  console.log('GameResultContainer rendering with:', {
    puzzleDate: puzzle?.date,
    userEmail: userEmail,
    resultToDisplay: resultToDisplay?.result,
    solveTime: resultToDisplay?.solveTime,
    hasUserSelections: !!(resultToDisplay?.selectedWords?.length),
    wasProvidedResult: !!gameResult,
    isViewingResults: isViewingResults(),
    isGameCompleted: puzzle?.date ? isGameCompleted(puzzle.date) : false,
    hasWordLinks: !!(puzzle?.wordLinks && puzzle.wordLinks.length > 0),
    wordLinks: puzzle?.wordLinks
  });

  if (!puzzle) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <p>No puzzle data available</p>
      </div>
    );
  }

  if (!resultToDisplay) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <p>Loading game results...</p>
      </div>
    );
  }

  // We're removing the back button option completely since users shouldn't be able to go back
  return (
    <GameResultScreen
      gameResult={resultToDisplay}
      puzzle={puzzle}
      filteredRules={filteredRules}
      isUsingExternalData={isUsingExternalData}
      // Not passing onGoBack prop to prevent navigation back
    />
  );
}
